import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { submitEligibility } from "@/lib/api";

const steps = [
  {
    key: "who",
    overline: "Step 01 / About you",
    title: "Which best describes you?",
    type: "single",
    options: ["An individual seeking balance", "A business leader", "HR / team lead", "Just exploring"],
  },
  {
    key: "resonates",
    overline: "Step 02 / Where you are",
    title: "Do any of these resonate?",
    type: "multi",
    options: [
      "I want to get the best out of my staff",
      "I want clarity on my goals and aspirations",
      "I feel overwhelmed and want balance",
      "I want a better culture in the business",
    ],
  },
  {
    key: "interests",
    overline: "Step 03 / What draws you",
    title: "What are you interested in?",
    type: "multi",
    options: ["1-to-1 Coaching", "Outdoor Adventure Days", "Corporate Programmes", "Keynotes & Workshops", "RX4 RESET Workshop", "Not sure yet"],
  },
  {
    key: "readiness",
    overline: "Step 04 / Your readiness",
    title: "Where are you on the journey?",
    type: "single",
    options: ["Ready to start now", "Actively exploring options", "Just curious for now"],
  },
];

const Apply = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ who: "", resonates: [], interests: [], readiness: "" });
  const [details, setDetails] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const current = steps[step];
  const isDetails = step === steps.length;
  const progress = Math.round(((step + (isDetails ? 1 : 0)) / (steps.length + 1)) * 100);

  const selectSingle = (v) => setAnswers({ ...answers, [current.key]: v });
  const toggleMulti = (v) => {
    const list = answers[current.key];
    setAnswers({ ...answers, [current.key]: list.includes(v) ? list.filter((x) => x !== v) : [...list, v] });
  };

  const canContinue = () => {
    if (isDetails) return details.name.trim() && details.email.trim();
    const val = answers[current.key];
    return current.type === "single" ? !!val : val.length > 0;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await submitEligibility({ ...answers, name: details.name, email: details.email, message: details.message || undefined });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <main data-testid="apply-page" className="min-h-screen flex items-center justify-center px-6 pt-20">
        <Reveal className="text-center max-w-2xl">
          <span className="inline-flex w-16 h-16 rounded-full bg-brand/10 border border-brand/30 items-center justify-center mb-8">
            <PartyPopper size={28} className="text-brand" strokeWidth={1.75} />
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-bone" data-testid="apply-success-message">
            We've got it, {details.name.split(" ")[0]}.
          </h1>
          <p className="mt-6 text-lg text-slate-400">
            Your application is with Dave. Expect a personal reply at <span className="text-bone font-medium">{details.email}</span>, usually within a couple of days.
          </p>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-brand">Make every day count</p>
        </Reveal>
      </main>
    );
  }

  return (
    <main data-testid="apply-page" className="min-h-screen flex flex-col justify-center px-6 md:px-12 lg:px-20 pt-32 pb-24">
      <div className="max-w-3xl w-full mx-auto">
        <div className="mb-12">
          <div className="flex justify-between items-baseline mb-3">
            <p className="overline-label" data-testid="apply-progress-label">Eligibility Check</p>
            <p className="font-mono text-xs text-slate-500" data-testid="apply-progress-percent">[ {progress}% ]</p>
          </div>
          <div className="h-1 bg-white/10 w-full rounded-full overflow-hidden">
            <motion.div
              className="h-1 bg-brand rounded-full"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              data-testid="apply-progress-bar"
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!isDetails ? (
            <motion.div
              key={current.key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="overline-label mb-4">{current.overline}</p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone mb-10" data-testid={`apply-step-title-${current.key}`}>
                {current.title}
              </h1>
              <div className="space-y-3">
                {current.options.map((opt, i) => {
                  const active = current.type === "single" ? answers[current.key] === opt : answers[current.key].includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => (current.type === "single" ? selectSingle(opt) : toggleMulti(opt))}
                      data-testid={`apply-option-${current.key}-${i}`}
                      className={`w-full flex items-center justify-between bg-ink-soft border rounded-xl px-6 py-5 text-left transition-colors duration-300 ${
                        active ? "border-brand bg-brand/10 text-bone" : "border-white/10 text-slate-400 hover:border-white/30"
                      }`}
                    >
                      <span className="text-base">{opt}</span>
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${active ? "border-brand bg-brand text-ink" : "border-white/30"}`}>
                        {active && <Check size={12} strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="overline-label mb-4">Final step / Your details</p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone mb-10" data-testid="apply-details-title">
                Where should Dave reach you?
              </h1>
              <div className="space-y-8">
                <input
                  data-testid="apply-name-input"
                  value={details.name}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                  placeholder="Your full name"
                  className="input-dark"
                />
                <input
                  type="email"
                  data-testid="apply-email-input"
                  value={details.email}
                  onChange={(e) => setDetails({ ...details, email: e.target.value })}
                  placeholder="Your email address"
                  className="input-dark"
                />
                <textarea
                  data-testid="apply-message-input"
                  rows={3}
                  value={details.message}
                  onChange={(e) => setDetails({ ...details, message: e.target.value })}
                  placeholder="Anything else you would like to share? (optional)"
                  className="input-dark resize-none"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between mt-12">
          <button
            type="button"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="apply-back-btn"
            className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500 hover:text-brand transition-colors duration-300 disabled:opacity-0"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <button
            type="button"
            onClick={() => (isDetails ? handleSubmit() : setStep(step + 1))}
            disabled={!canContinue() || loading}
            data-testid="apply-next-btn"
            className="btn-primary group disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isDetails ? (loading ? "Submitting..." : "Submit Application") : "Continue"}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </main>
  );
};

export default Apply;
